import { defineNuxtPlugin } from '#imports'

export default defineNuxtPlugin(() => {
  // OneSignal Web SDK 透過 CDN script 載入 (在 nuxt.config 或 index.html 中)
  // 此 plugin 提供 Vue instance 可用的 $OneSignal 存取

  if (!import.meta.client) return

  // 快取 OneSignal promise，避免重複註冊 deferred callback
  let oneSignalPromise = null

  // OneSignal v16 透過 OneSignalDeferred callback 提供 SDK instance
  const waitForOneSignal = () => {
    if (oneSignalPromise) {
      return oneSignalPromise
    }

    window.OneSignalDeferred = window.OneSignalDeferred || []
    oneSignalPromise = new Promise((resolve) => {
      let timeoutId = null
      let resolved = false

      const cleanup = () => {
        if (timeoutId) {
          clearTimeout(timeoutId)
          timeoutId = null
        }
      }

      const safeResolve = (value) => {
        if (resolved) return
        resolved = true
        cleanup()
        resolve(value)
      }

      timeoutId = setTimeout(() => {
        console.warn('[OneSignal] SDK not loaded within 30 seconds')
        safeResolve(null)
      }, 30000)

      window.OneSignalDeferred.push((OneSignal) => {
        safeResolve(OneSignal)
      })
    })

    return oneSignalPromise
  }

  // init 完成閘門：login / logout 必須等到 OneSignal.init() 完成 (SDK 核心 _coreDirector 就緒)
  // 才能呼叫，否則 SDK 內部會存取尚未建立的 _coreDirector 而拋出未捕捉的 rejection (Sentry CLIENT-PWA-SSR-RJ)
  let isInitialized = false
  let initGateSettled = false
  let resolveInitGate = null
  const initGate = new Promise((resolve) => {
    resolveInitGate = resolve
  })
  const settleInitGate = () => {
    if (initGateSettled) return
    initGateSettled = true
    resolveInitGate()
  }

  return {
    provide: {
      OneSignal: {
        async init(config) {
          if (isInitialized) return false

          const OneSignal = await waitForOneSignal()
          if (!OneSignal) {
            console.error('[OneSignal] SDK not available')
            settleInitGate()
            return false
          }

          try {
            await OneSignal.init(config)
            isInitialized = true
            console.log('[OneSignal] Initialized successfully')
            return true
          } catch (error) {
            // HMR 或 plugin 模組重建時，window.OneSignal 仍存活但閉包 isInitialized 被重置，
            // SDK 拋出 "SDK already initialized"；此時視為已初始化，不視為錯誤
            if (error?.message?.includes('already initialized')) {
              isInitialized = true
              return false
            } else {
              console.error('[OneSignal] Initialization error:', error)
              throw error
            }
          } finally {
            settleInitGate()
          }
        },

        async login(externalId) {
          const OneSignal = await waitForOneSignal()
          if (!OneSignal) return false

          // 等待 init 完成，確保 SDK 核心已就緒；init 失敗時不呼叫 login 以免崩潰
          if (!isInitialized) await initGate
          if (!isInitialized) return false

          try {
            await OneSignal.login(externalId)
            console.log(`[OneSignal] User logged in: ${externalId}`)
            return true
          } catch (error) {
            console.error('[OneSignal] Login error:', error)
            return false
          }
        },

        async logout() {
          const OneSignal = await waitForOneSignal()
          if (!OneSignal) return

          // 等待 init 完成，確保 SDK 核心已就緒；init 失敗時不呼叫 logout 以免崩潰
          if (!isInitialized) await initGate
          if (!isInitialized) return

          try {
            await OneSignal.logout()
            console.log('[OneSignal] User logged out')
          } catch (error) {
            console.error('[OneSignal] Logout error:', error)
          }
        },

        // 直接存取 OneSignal SDK
        async getSDK() {
          return await waitForOneSignal()
        },

        // 等待 SDK 載入且 init() 完成後才回傳；供需要 init 後狀態的呼叫方使用
        async getInitializedSDK() {
          const OneSignal = await waitForOneSignal()
          if (!OneSignal) return null
          if (!isInitialized) await initGate
          if (!isInitialized) return null
          return OneSignal
        },
      },
    },
  }
})
